import { useState, useCallback, useEffect } from 'react';
import { apiClient, API_BASE_URL, CapacityCheck } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import type { CampaignTemplate } from '../../types/campaignTemplate';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import WhatsAppPreview from './WhatsAppPreview';
import DispatchLimitIndicator from './DispatchLimitIndicator';
import { AlertTriangle } from 'lucide-react';

interface ExecuteTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: CampaignTemplate;
  onSuccess: (campaignId: string) => void;
  qualityRating?: string;
  accountStatus?: string;
}

type ExecutionStep = 'info' | 'upload' | 'schedule' | 'review';
type ScheduleType = 'immediate' | 'scheduled';

interface ExecutionConfig {
  executionName: string;
  globalParams: Record<string, any>;
  file: File | null;
  scheduleType: ScheduleType;
  scheduledAt: string; // ISO 8601 datetime
}

interface ValidationError {
  field: string;
  message: string;
}

// WhatsApp Template Structure
interface TemplateButton {
  type: 'URL' | 'QUICK_REPLY' | 'PHONE_NUMBER';
  text: string;
  url?: string;
  example?: string[];
}

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: string;
  text?: string;
  buttons?: TemplateButton[];
  example?: {
    header_text?: string[][];
    header_handle?: string[];
    body_text?: string[][];
  };
}

interface WhatsAppTemplate {
  id: string;
  name: string;
  language: string;
  components: TemplateComponent[];
}

export function ExecuteTemplateModal({
  isOpen,
  onClose,
  template,
  onSuccess,
  qualityRating,
  accountStatus,
}: ExecuteTemplateModalProps) {
  const { accessToken } = useAuth();
  const [currentStep, setCurrentStep] = useState<ExecutionStep>('info');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);

  // Verificar se a qualidade ou conta está bloqueada
  const isQualityBlocked = qualityRating?.toLowerCase() === 'low' || qualityRating?.toLowerCase() === 'red';
  const accStatus = accountStatus?.toUpperCase();
  const isAccountBlocked = accStatus === 'BANNED' || accStatus === 'BLOCKED';
  const isDispatchBlocked = isQualityBlocked || isAccountBlocked;

  // WhatsApp template structure
  const [whatsappTemplate, setWhatsappTemplate] = useState<WhatsAppTemplate | null>(null);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);

  // Helper: Filter out body variables from global params
  const filterBodyVariables = (params: Record<string, any>): Record<string, any> => {
    const filtered: Record<string, any> = {};
    Object.entries(params || {}).forEach(([key, value]) => {
      if (!key.startsWith('body_')) {
        filtered[key] = value;
      }
    });
    return filtered;
  };

  // Form state
  const [config, setConfig] = useState<ExecutionConfig>({
    executionName: `${template.name} - ${new Date().toLocaleString('pt-BR')}`,
    globalParams: filterBodyVariables(template.defaultGlobalParams || {}),
    file: null,
    scheduleType: 'immediate',
    // Default: 1 hour from now no fuso horário de São Paulo
    scheduledAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
  });

  // CSV preview
  const [validRecipients, setValidRecipients] = useState<number>(0);
  const [invalidRecipients, setInvalidRecipients] = useState<number>(0);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  // Dispatch capacity
  const [capacityInfo, setCapacityInfo] = useState<CapacityCheck | null>(null);
  const [isLoadingCapacity, setIsLoadingCapacity] = useState(false);

  // Duplicate warning modal
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateData, setDuplicateData] = useState<any>(null);
  const [userConfirmed, setUserConfirmed] = useState(false);

  // Fetch WhatsApp template structure to identify global vs individual params
  useEffect(() => {
    if (isOpen && accessToken && template.wabaAccountId) {
      fetchWhatsAppTemplate();
    }
  }, [isOpen, template.templateName, template.templateLanguage, template.wabaAccountId, accessToken]);

  const fetchWhatsAppTemplate = async () => {
    if (!accessToken) return;

    const requestPayload = {
      wabaAccountId: template.wabaAccountId,
      name: template.templateName,
      language: template.templateLanguage || 'pt_BR', // Usar pt_BR como fallback
    };

    setIsLoadingTemplate(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/templates/find-by-name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(requestPayload),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data && result.data.template) {
          setWhatsappTemplate(result.data.template);
        }
      } else if (response.status === 404) {
        // Template not found - may need to be synced
      } else {
        // Other HTTP error
      }
    } catch {
      // Request error - template preview won't be available
    } finally {
      setIsLoadingTemplate(false);
    }
  };

  // Extract variable indices from template text (e.g., "Hello {{1}}" -> [1])
  const extractVariableIndices = (text: string | undefined): number[] => {
    if (!text) return [];
    const matches = text.matchAll(/\{\{(\d+)\}\}/g);
    return Array.from(matches, m => parseInt(m[1], 10));
  };

  // Check if template has URL buttons with dynamic parameters
  const hasUrlButtonsWithParams = (): boolean => {
    if (!whatsappTemplate) return false;

    const buttonsComponent = whatsappTemplate.components.find(c => c.type === 'BUTTONS');
    if (!buttonsComponent?.buttons) return false;

    // Check if there's any URL button with dynamic parameters ({{1}})
    return buttonsComponent.buttons.some(btn =>
      btn.type === 'URL' && btn.url && btn.url.includes('{{')
    );
  };

  // Filter global params to only show HEADER and FOOTER variables
  const getEditableGlobalParams = (): Record<string, any> => {
    if (!whatsappTemplate || !config.globalParams) return {};

    const editableParams: Record<string, any> = {};

    // Get HEADER and FOOTER components
    const headerComponent = whatsappTemplate.components.find(c => c.type === 'HEADER');
    const footerComponent = whatsappTemplate.components.find(c => c.type === 'FOOTER');

    // Extract variable indices from HEADER and FOOTER
    // BLOQUEIO TEMPORÁRIO: Não extrair variáveis de header TEXT (mesmo para templates antigos)
    const headerVarIndices: number[] = []; // Desabilitado: extractVariableIndices(headerComponent?.text);
    // BLOQUEIO: Footer também não aceita mais variáveis
    const footerVarIndices: number[] = []; // Desabilitado: extractVariableIndices(footerComponent?.text);
    const globalVarIndices = [...headerVarIndices, ...footerVarIndices];

    // Filter globalParams to only include HEADER/FOOTER variables
    Object.entries(config.globalParams).forEach(([key, value]) => {
      // EXCLUIR variáveis de BODY - essas são preenchidas via CSV
      if (key.startsWith('body_')) {
        return; // Skip body variables
      }

      // Check if the key represents a global variable (numbered like "1", "2", etc.)
      const varIndex = parseInt(key, 10);
      if (!isNaN(varIndex) && globalVarIndices.includes(varIndex)) {
        editableParams[key] = value;
      } else if (isNaN(parseInt(key, 10))) {
        // Include non-numeric keys (custom global params like header_media, button_url_suffix, etc)
        editableParams[key] = value;
      }
    });

    return editableParams;
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('info');
      setConfig({
        executionName: `${template.name} - ${new Date().toLocaleString('pt-BR')}`,
        globalParams: filterBodyVariables(template.defaultGlobalParams || {}),
        file: null,
        scheduleType: 'immediate',
        scheduledAt: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
      });
      setValidRecipients(0);
      setInvalidRecipients(0);
      setCsvPreview([]);
      setErrors([]);
      setWhatsappTemplate(null);
      setFileInputKey((prev) => prev + 1); // Reset file input
      setShowDuplicateWarning(false);
      setDuplicateData(null);
      setUserConfirmed(false);
    }
  }, [isOpen, template]);

  // Extrair variáveis do body do template dinamicamente
  const getBodyVariables = (): string[] => {
    if (!whatsappTemplate) {
      // Fallback: se template não foi carregado, usar variáveis genéricas
      return ['var1', 'var2'];
    }

    const bodyComponent = whatsappTemplate.components.find(c => c.type === 'BODY');
    if (!bodyComponent?.text) return [];

    const matches = bodyComponent.text.matchAll(/\{\{(\d+)\}\}/g);
    const variables: string[] = [];

    for (const match of matches) {
      const varNum = match[1];
      variables.push(`variavel_${varNum}`);
    }

    return variables.length > 0 ? variables : ['var1', 'var2']; // Fallback se não houver variáveis
  };

  const downloadCSVTemplate = () => {
    // Colunas fixas
    const fixedColumns = ['telefone', 'nome'];

    // Colunas dinâmicas baseadas nas variáveis do template
    const bodyVars = getBodyVariables();
    const allColumns = [...fixedColumns, ...bodyVars];

    // Criar header
    const header = allColumns.join(',');

    // Criar linhas de exemplo (mostrando ambos formatos: com e sem DDI)
    const example1Values = allColumns.map((col, idx) => {
      if (col === 'telefone') return '5511999999999'; // Com DDI
      if (col === 'nome') return 'João Silva';
      return `Valor${idx - 1}`; // Exemplo: Valor1, Valor2, etc
    });

    const example2Values = allColumns.map((col, idx) => {
      if (col === 'telefone') return '11888888888'; // Sem DDI (será convertido para +5511888888888)
      if (col === 'nome') return 'Maria Santos';
      return `Valor${idx + 1}`; // Exemplo: Valor3, Valor4, etc
    });

    const csvContent = `${header}\n${example1Values.join(',')}\n${example2Values.join(',')}`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `modelo_${template.name}_destinatarios.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadXLSXTemplate = () => {
    // Colunas fixas
    const fixedColumns = ['telefone', 'nome'];

    // Colunas dinâmicas baseadas nas variáveis do template
    const bodyVars = getBodyVariables();
    const allColumns = [...fixedColumns, ...bodyVars];

    // Criar dados (mostrando ambos formatos: com e sem DDI)
    const data = [
      allColumns, // Header
      allColumns.map((col, idx) => {
        if (col === 'telefone') return '5511999999999'; // Com DDI
        if (col === 'nome') return 'João Silva';
        return `Valor${idx - 1}`;
      }),
      allColumns.map((col, idx) => {
        if (col === 'telefone') return '11888888888'; // Sem DDI (será convertido para +5511888888888)
        if (col === 'nome') return 'Maria Santos';
        return `Valor${idx + 1}`;
      }),
    ];

    // Criar workbook e worksheet
    const ws = XLSX.utils.aoa_to_sheet(data);

    // IMPORTANTE: Formatar TODAS as colunas como TEXTO para evitar notação científica
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) continue;

        // Forçar formato de texto
        ws[cellAddress].t = 's'; // 's' = string
        ws[cellAddress].z = '@'; // '@' = formato de texto
      }
    }

    // Definir largura das colunas
    ws['!cols'] = allColumns.map(() => ({ wch: 20 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Destinatários');

    // Fazer download
    XLSX.writeFile(wb, `modelo_${template.name}_destinatarios.xlsx`, {
      bookType: 'xlsx',
      cellStyles: true,
    });
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Aceita números com ou sem + no início
    // Exemplos válidos: +5511999999999, 5511999999999, 11999999999
    const cleaned = phone.trim();

    // Remove o + se existir
    const withoutPlus = cleaned.startsWith('+') ? cleaned.substring(1) : cleaned;

    // Verifica se tem apenas dígitos
    if (!/^\d+$/.test(withoutPlus)) {
      return false;
    }

    // Deve ter entre 10 e 15 dígitos (padrão internacional)
    // 10-11 dígitos: número brasileiro sem DDI
    // 12-15 dígitos: número com DDI
    return withoutPlus.length >= 10 && withoutPlus.length <= 15;
  };

  const handleFileChange = useCallback((file: File | null) => {
    setConfig((prev) => ({ ...prev, file }));

    if (!file) {
      setCsvPreview([]);
      setValidRecipients(0);
      setInvalidRecipients(0);
      // Reset file input by changing key
      setFileInputKey((prev) => prev + 1);
      return;
    }

    const isCSV = file.name.endsWith('.csv') || file.type === 'text/csv';
    const isExcel = file.name.endsWith('.xlsx') ||
      file.type === 'application/vnd.ms-excel' ||
      file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

    if (isCSV) {
      // Parse CSV with PapaParse
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;

        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim().toLowerCase(),
          complete: (results) => {
            const data = results.data as any[];

            if (data.length === 0) {
              setCsvPreview([]);
              setValidRecipients(0);
              setInvalidRecipients(0);
              return;
            }

            const headers = Object.keys(data[0] || {});

            // Criar preview com headers + primeiras 5 linhas
            const preview: string[][] = [headers];
            data.slice(0, 5).forEach(row => {
              const rowValues = headers.map(h => String(row[h] || ''));
              preview.push(rowValues);
            });
            setCsvPreview(preview);

            // Validar números de telefone
            const phoneKey = headers.find(h =>
              h === 'phone' || h === 'telefone' || h === 'numero' || h === 'number'
            );

            let valid = 0;
            let invalid = 0;

            if (phoneKey) {
              data.forEach(row => {
                const phone = row[phoneKey] || '';
                if (phone && validatePhoneNumber(phone)) {
                  valid++;
                } else if (phone) {
                  invalid++;
                }
              });
            }

            setValidRecipients(valid);
            setInvalidRecipients(invalid);
          },
        });
      };
      reader.readAsText(file);
    } else if (isExcel) {
      // Parse Excel with xlsx
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const buffer = e.target?.result as ArrayBuffer;
          const workbook = XLSX.read(buffer, { type: 'array' });

          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          const data = XLSX.utils.sheet_to_json(worksheet, {
            raw: false,
            defval: '',
          }) as any[];

          if (data.length === 0) {
            setCsvPreview([]);
            setValidRecipients(0);
            setInvalidRecipients(0);
            return;
          }

          // Normalizar headers
          const normalizedData = data.map((row) => {
            const normalizedRow: Record<string, any> = {};
            for (const [key, value] of Object.entries(row)) {
              normalizedRow[key.trim().toLowerCase()] = value;
            }
            return normalizedRow;
          });

          const headers = Object.keys(normalizedData[0] || {});

          // Criar preview
          const preview: string[][] = [headers];
          normalizedData.slice(0, 5).forEach(row => {
            const rowValues = headers.map(h => String(row[h] || ''));
            preview.push(rowValues);
          });
          setCsvPreview(preview);

          // Validar números de telefone
          const phoneKey = headers.find(h =>
            h === 'phone' || h === 'telefone' || h === 'numero' || h === 'number'
          );

          let valid = 0;
          let invalid = 0;

          if (phoneKey) {
            normalizedData.forEach(row => {
              const phone = String(row[phoneKey] || '').trim();
              if (phone && validatePhoneNumber(phone)) {
                valid++;
              } else if (phone) {
                invalid++;
              }
            });
          }

          setValidRecipients(valid);
          setInvalidRecipients(invalid);
        } catch {
          setCsvPreview([]);
          setValidRecipients(0);
          setInvalidRecipients(0);
        }
      };
      reader.readAsArrayBuffer(file);
    }
  }, []);

  const validateStep = (step: ExecutionStep): boolean => {
    const newErrors: ValidationError[] = [];

    switch (step) {
      case 'info':
        if (!config.executionName.trim()) {
          newErrors.push({
            field: 'executionName',
            message: 'Nome da execução é obrigatório',
          });
        }
        break;

      case 'upload':
        if (!config.file) {
          newErrors.push({
            field: 'file',
            message: 'Arquivo CSV é obrigatório',
          });
        }
        if (validRecipients === 0) {
          newErrors.push({
            field: 'recipients',
            message: 'Nenhum destinatário válido encontrado no arquivo',
          });
        }
        break;

      case 'schedule':
        if (config.scheduleType === 'scheduled') {
          const scheduledDate = new Date(config.scheduledAt);
          const now = new Date();
          if (scheduledDate <= now) {
            newErrors.push({
              field: 'scheduledAt',
              message: 'Data agendada deve ser no futuro',
            });
          }
        }
        break;
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Check dispatch capacity when entering review step
  const checkCapacity = async () => {
    if (!accessToken || !template.wabaAccountId || validRecipients === 0) return;

    setIsLoadingCapacity(true);
    try {
      const response = await apiClient.checkDispatchCapacity(
        template.wabaAccountId,
        validRecipients,
        accessToken
      );

      if (response.success) {
        setCapacityInfo(response.data);
      }
    } catch {
      // Failed to check dispatch capacity
    } finally {
      setIsLoadingCapacity(false);
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) return;

    const steps: ExecutionStep[] = ['info', 'upload', 'schedule', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      const nextStepValue = steps[currentIndex + 1];
      setCurrentStep(nextStepValue);

      // Check capacity when entering review step
      if (nextStepValue === 'review') {
        checkCapacity();
      }
    }
  };

  const prevStep = () => {
    const steps: ExecutionStep[] = ['info', 'upload', 'schedule', 'review'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  const handleSubmit = async () => {
    if (!accessToken || !config.file) return;

    // 🔒 PROTEÇÃO ANTI-DOUBLE-CLICK: Bloquear se já está processando ou clicou recentemente
    const now = Date.now();
    if (isSubmitting) {
      return;
    }

    if (now - lastSubmitTime < 5000) {
      setErrors([{
        field: 'general',
        message: '⏳ Aguarde alguns segundos antes de tentar novamente.',
      }]);
      return;
    }

    setLastSubmitTime(now);
    setIsSubmitting(true);
    setErrors([]);

    try {
      // ⚠️ PROTEÇÃO 2: Validar duplicação de destinatários antes de executar
      if (!userConfirmed) {
        const formData = new FormData();
        formData.append('file', config.file);

        const validationResponse = await fetch(
          `${API_BASE_URL}/api/messaging/campaign-templates/${template.id}/validate-recipients`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            body: formData,
          }
        );

        if (validationResponse.ok) {
          const validationData = await validationResponse.json();

          // Se requer confirmação (>50% duplicado), mostrar modal de aviso
          if (validationData.requiresConfirmation) {
            setDuplicateData(validationData);
            setShowDuplicateWarning(true);
            setIsSubmitting(false);
            return; // Parar execução até confirmação do usuário
          }
        }
        // Se falhou a validação ou não requer confirmação, continuar normalmente
      }

      // Execute template with scheduling support
      const response = await apiClient.executeTemplate(
        template.id,
        config.file,
        {
          executionName: config.executionName,
          globalParams: config.globalParams,
          // TODO: Add scheduledAt support when backend is updated
        },
        accessToken
      );

      if (response.success && response.data) {
        onSuccess(response.data.id);
        onClose();
      }
    } catch (error: any) {
      // ⚠️ PROTEÇÃO 1: Detectar erro de campanha em andamento ou conflito (409)
      const errorMessage = error.message || 'Erro ao executar campanha';
      const statusCode = error.status || error.statusCode || 0;
      const isActiveCampaignError = errorMessage.includes('disparo em andamento');
      const isConflictError = statusCode === 409 ||
                              errorMessage.includes('já está em andamento') ||
                              errorMessage.includes('menos de 30 segundos');

      let displayMessage = errorMessage;

      if (isConflictError) {
        displayMessage = `⚠️ ${errorMessage}\n\nIsso pode acontecer se você clicou mais de uma vez. Verifique o histórico de execuções.`;
      } else if (isActiveCampaignError) {
        displayMessage = `❌ ${errorMessage}\n\nAguarde o término da campanha atual ou cancele-a antes de iniciar uma nova.`;
      }

      setErrors([
        {
          field: 'general',
          message: displayMessage,
        },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateGlobalParam = (key: string, value: any) => {
    setConfig((prev) => ({
      ...prev,
      globalParams: {
        ...prev.globalParams,
        [key]: value,
      },
    }));
  };

  const removeGlobalParam = (key: string) => {
    setConfig((prev) => {
      const newParams = { ...prev.globalParams };
      delete newParams[key];
      return {
        ...prev,
        globalParams: newParams,
      };
    });
  };

  if (!isOpen) return null;

  // Extract template components for preview
  const headerComponent = whatsappTemplate?.components.find(c => c.type === 'HEADER');
  const bodyComponent = whatsappTemplate?.components.find(c => c.type === 'BODY');
  const footerComponent = whatsappTemplate?.components.find(c => c.type === 'FOOTER');
  const buttonsComponent = whatsappTemplate?.components.find(c => c.type === 'BUTTONS');

  const bodyExamples = bodyComponent?.example?.body_text?.[0] || [];
  const headerExample = headerComponent?.example?.header_text?.[0]?.[0] || '';
  const headerMediaUrl = headerComponent?.example?.header_handle?.[0] || '';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-bg-1 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-bg-1 border-b border-border-1 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-text-1">Executar Campanha</h2>
            <p className="text-sm text-text-2 mt-1">{template.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-text-3 hover:text-text-2 text-2xl leading-none"
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 bg-bg-2 border-b border-border-1">
          <div className="flex items-center justify-between">
            {(['info', 'upload', 'schedule', 'review'] as ExecutionStep[]).map((step, idx) => {
              const stepLabels = {
                info: 'Informações',
                upload: 'Upload CSV',
                schedule: 'Agendamento',
                review: 'Revisar',
              };
              const currentIdx = ['info', 'upload', 'schedule', 'review'].indexOf(currentStep);
              const isActive = currentStep === step;
              const isCompleted = idx < currentIdx;

              return (
                <div key={step} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        isCompleted
                          ? 'bg-success text-white'
                          : isActive
                          ? 'bg-accent-1 text-white'
                          : 'bg-bg-3 text-text-3'
                      }`}
                    >
                      {isCompleted ? '✓' : idx + 1}
                    </div>
                    <span className="text-xs mt-1 font-medium">{stepLabels[step]}</span>
                  </div>
                  {idx < 3 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        isCompleted ? 'bg-success' : 'bg-bg-3'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="mx-6 mt-4 bg-danger/10 border border-danger/20 rounded-lg p-4">
            <div className="flex items-start">
              <span className="text-danger mr-2">⚠️</span>
              <div className="flex-1">
                <p className="text-sm font-semibold text-danger mb-1">Erros encontrados:</p>
                <ul className="list-disc list-inside text-sm text-danger/80">
                  {errors.map((error, idx) => (
                    <li key={idx}>{error.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Aviso de Qualidade Baixa */}
        {isDispatchBlocked && (
          <div className="mx-6 mt-4 bg-danger/10 border border-danger rounded-lg p-4 flex items-center gap-3">
            <AlertTriangle className="text-danger flex-shrink-0" size={20} />
            <div>
              <p className="text-danger font-medium">
                Conta com Qualidade Baixa - Execução Bloqueada
              </p>
              <p className="text-danger/80 text-sm">
                Esta conta está com qualidade BAIXA. Não é possível executar campanhas até a qualidade melhorar.
              </p>
            </div>
          </div>
        )}

        {/* Main Content with Sidebar */}
        <div className="flex gap-6 px-6 py-6">
          {/* Sidebar Esquerda - Preview WhatsApp */}
          <div className="w-[400px] flex-shrink-0">
            <div className="sticky top-6">
              <h3 className="text-sm font-semibold text-text-2 mb-3">
                📱 Preview do Template
              </h3>
              {whatsappTemplate ? (
                <WhatsAppPreview
                  headerFormat={headerComponent?.format as any}
                  headerText={headerComponent?.format === 'TEXT' ? headerComponent?.text : undefined}
                  headerExample={headerExample}
                  headerMediaUrl={headerMediaUrl}
                  bodyText={bodyComponent?.text || ''}
                  bodyExamples={bodyExamples}
                  footerText={footerComponent?.text}
                  buttons={buttonsComponent?.buttons || []}
                  highlightVariables
                />
              ) : isLoadingTemplate ? (
                <div className="bg-bg-3 rounded-lg p-8 text-center">
                  <span className="animate-spin inline-block text-2xl">⏳</span>
                  <p className="text-sm text-text-3 mt-2">Carregando preview...</p>
                </div>
              ) : (
                <div className="bg-bg-3 rounded-lg p-8 text-center">
                  <p className="text-sm text-text-3">Preview não disponível</p>
                </div>
              )}
            </div>
          </div>

          {/* Área Central - Conteúdo do Step */}
          <div className="flex-1">
            {/* STEP 1: Info + Global Params */}
            {currentStep === 'info' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">
                  Nome desta Execução
                </label>
                <input
                  type="text"
                  value={config.executionName}
                  onChange={(e) =>
                    setConfig((prev) => ({ ...prev, executionName: e.target.value }))
                  }
                  className="w-full px-4 py-2 border border-border-1 rounded-lg focus:ring-2 focus:ring-accent-1 focus:border-transparent"
                  placeholder="Ex: Black Friday - VIP Customers"
                />
                <p className="text-xs text-text-3 mt-1">
                  Nome para identificar esta execução específica
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">
                  Parâmetros Globais (Opcional)
                </label>

                {isLoadingTemplate ? (
                  <div className="bg-bg-2 rounded-lg p-8 text-center">
                    <span className="animate-spin inline-block">⏳</span>
                    <p className="text-sm text-text-3 mt-2">Carregando estrutura do template...</p>
                  </div>
                ) : (
                  <>
                    {/* HEADER Override Section */}
                    {whatsappTemplate?.components.some(c => c.type === 'HEADER') && (
                      <div className="bg-info/10 border border-info/20 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2 mb-3">
                          <span className="text-accent-1">ℹ️</span>
                          <div>
                            <p className="text-sm font-semibold text-text-1">Override de HEADER (Opcional)</p>
                            <p className="text-xs text-accent-1 mt-1">
                              O template possui um HEADER com mídia estática. Você pode sobrescrever com URLs customizadas:
                            </p>
                          </div>
                        </div>

                        <div className="space-y-3 mt-3">
                          {/* IMAGE Override */}
                          {whatsappTemplate.components.find(c => c.type === 'HEADER' && c.format === 'IMAGE') && (
                            <div>
                              <label className="block text-xs font-medium text-text-2 mb-1">
                                🖼️ Override - URL da Imagem (HEADER)
                              </label>
                              <input
                                type="url"
                                placeholder="https://exemplo.com/imagem-customizada.jpg"
                                value={config.globalParams.header_image_url || ''}
                                onChange={(e) => updateGlobalParam('header_image_url', e.target.value)}
                                className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                              />
                              <p className="text-xs text-text-3 mt-1">
                                Deixe vazio para usar a imagem padrão do template
                              </p>
                            </div>
                          )}

                          {/* VIDEO Override */}
                          {whatsappTemplate.components.find(c => c.type === 'HEADER' && c.format === 'VIDEO') && (
                            <div>
                              <label className="block text-xs font-medium text-text-2 mb-1">
                                🎥 Override - URL do Vídeo (HEADER)
                              </label>
                              <input
                                type="url"
                                placeholder="https://exemplo.com/video-customizado.mp4"
                                value={config.globalParams.header_video_url || ''}
                                onChange={(e) => updateGlobalParam('header_video_url', e.target.value)}
                                className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                              />
                              <p className="text-xs text-text-3 mt-1">
                                Deixe vazio para usar o vídeo padrão do template
                              </p>
                            </div>
                          )}

                          {/* DOCUMENT Override */}
                          {whatsappTemplate.components.find(c => c.type === 'HEADER' && c.format === 'DOCUMENT') && (
                            <div className="space-y-2">
                              <div>
                                <label className="block text-xs font-medium text-text-2 mb-1">
                                  📄 Override - URL do Documento (HEADER)
                                </label>
                                <input
                                  type="url"
                                  placeholder="https://exemplo.com/documento-customizado.pdf"
                                  value={config.globalParams.header_document_url || ''}
                                  onChange={(e) => updateGlobalParam('header_document_url', e.target.value)}
                                  className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-text-2 mb-1">
                                  Nome do Arquivo
                                </label>
                                <input
                                  type="text"
                                  placeholder="documento.pdf"
                                  value={config.globalParams.header_document_filename || ''}
                                  onChange={(e) => updateGlobalParam('header_document_filename', e.target.value)}
                                  className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                                />
                                <p className="text-xs text-text-3 mt-1">
                                  Deixe vazio para usar o documento padrão do template
                                </p>
                              </div>
                            </div>
                          )}

                          {/* TEXT Override - DESABILITADO TEMPORARIAMENTE */}
                          {/* BLOQUEIO: Header de texto com variáveis não é mais permitido */}
                          {/* {whatsappTemplate.components.find(c => c.type === 'HEADER' && c.format === 'TEXT') && extractVariableIndices(whatsappTemplate.components.find(c => c.type === 'HEADER')?.text).length > 0 && (
                            <div>
                              <label className="block text-xs font-medium text-text-2 mb-1">
                                💬 Override - Texto (HEADER)
                              </label>
                              <input
                                type="text"
                                placeholder="Texto customizado para o header"
                                value={config.globalParams.header_text || ''}
                                onChange={(e) => updateGlobalParam('header_text', e.target.value)}
                                className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                              />
                              <p className="text-xs text-text-3 mt-1">
                                Valor dinâmico para o header de texto
                              </p>
                            </div>
                          )} */}
                        </div>
                      </div>
                    )}

                    {/* BUTTON Override Section - Only show for URL buttons with dynamic params */}
                    {hasUrlButtonsWithParams() && (
                      <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-4">
                        <div className="flex items-start gap-2 mb-3">
                          <span className="text-success">🔘</span>
                          <div>
                            <p className="text-sm font-semibold text-text-1">Override de BOTÕES URL (Opcional)</p>
                            <p className="text-xs text-success mt-1">
                              O template possui botões com URL dinâmica. Você pode customizar o parâmetro:
                            </p>
                          </div>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-text-2 mb-1">
                            🔗 Sufixo da URL do Botão (Dinâmico)
                          </label>
                          <input
                            type="text"
                            placeholder="/caminho-dinamico ou ?param=valor"
                            value={config.globalParams.button_url_suffix || ''}
                            onChange={(e) => updateGlobalParam('button_url_suffix', e.target.value)}
                            className="w-full px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-success"
                          />
                          <p className="text-xs text-text-3 mt-1">
                            Parte dinâmica da URL que será concatenada ao botão (ex: se a URL base é https://exemplo.com, você pode adicionar /produto/123)
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Existing Global Params (Header/Footer text variables) */}
                    <div className="bg-bg-2 rounded-lg p-4 space-y-2 max-h-64 overflow-y-auto">
                      {Object.entries(getEditableGlobalParams()).filter(([key]) =>
                        !['header_image_url', 'header_video_url', 'header_document_url', 'header_document_filename', 'header_text', 'header_media', 'button_url_suffix'].includes(key) &&
                        !key.startsWith('header_') // BLOQUEIO: Não permitir variáveis de header TEXT (header_1, header_2, etc)
                      ).map(([key, value]) => (
                        <div key={key} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={key}
                            disabled
                            className="flex-1 px-3 py-2 bg-bg-3 border border-border-1 rounded text-sm"
                          />
                          <span className="text-text-3">=</span>
                          <input
                            type="text"
                            value={value}
                            onChange={(e) => updateGlobalParam(key, e.target.value)}
                            className="flex-1 px-3 py-2 border border-border-1 rounded text-sm focus:ring-2 focus:ring-accent-1"
                          />
                          <button
                            onClick={() => removeGlobalParam(key)}
                            className="px-3 py-2 text-danger hover:bg-danger/10 rounded text-sm"
                          >
                            Remover
                          </button>
                        </div>
                      ))}

                      {Object.entries(getEditableGlobalParams()).filter(([key]) =>
                        !['header_image_url', 'header_video_url', 'header_document_url', 'header_document_filename', 'header_text', 'header_media', 'button_url_suffix'].includes(key) &&
                        !key.startsWith('header_') // BLOQUEIO: Não permitir variáveis de header TEXT (header_1, header_2, etc)
                      ).length === 0 && (
                        <p className="text-sm text-text-3 text-center py-4">
                          Nenhum parâmetro adicional de texto definido
                        </p>
                      )}
                    </div>

                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-text-3">
                        ℹ️ Apenas <strong>Body</strong> aceita variáveis dinâmicas (via CSV)
                      </p>
                      <p className="text-xs text-text-3">
                        ℹ️ <strong>Header</strong> pode ter mídia (IMAGEM/VÍDEO/DOCUMENTO)
                      </p>
                      <p className="text-xs text-text-3">
                        ℹ️ <strong>Footer</strong> é sempre texto estático
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP 2: Upload CSV */}
          {currentStep === 'upload' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-2 mb-2">
                  Arquivo de Destinatários (CSV/Excel)
                </label>
                <div className="border-2 border-dashed border-border-1 rounded-lg p-8 text-center hover:border-accent-1 transition-colors">
                  <input
                    key={fileInputKey}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <svg
                      className="w-12 h-12 text-text-3 mb-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    <span className="text-accent-1 font-medium">Clique para selecionar</span>
                    <span className="text-text-3 text-sm mt-1">ou arraste o arquivo aqui</span>
                  </label>
                </div>

                {config.file && (
                  <div className="mt-4 bg-info/10 border border-info/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-accent-1">📄</span>
                        <div>
                          <p className="text-sm font-medium text-text-1">{config.file.name}</p>
                          <p className="text-xs text-accent-1">
                            {(config.file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleFileChange(null)}
                        className="text-accent-1 hover:text-accent-1/80 text-sm"
                      >
                        Remover
                      </button>
                    </div>

                    {validRecipients > 0 && (
                      <div className="mt-4 pt-4 border-t border-info/20">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-xs text-accent-1">Válidos</p>
                            <p className="text-2xl font-bold text-success">{validRecipients}</p>
                          </div>
                          {invalidRecipients > 0 && (
                            <div>
                              <p className="text-xs text-accent-1">Inválidos</p>
                              <p className="text-2xl font-bold text-danger">{invalidRecipients}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Instruções de formato do arquivo */}
              <div className="bg-bg-2 border border-border-1 rounded-lg p-4">
                <p className="text-text-1 font-medium mb-2">Formato do Arquivo</p>
                <p className="text-text-2 text-sm mb-2">
                  O arquivo deve conter as seguintes colunas (nesta ordem):
                </p>
                <ul className="text-text-2 text-sm list-disc list-inside space-y-1">
                  <li><strong>1ª coluna: telefone</strong> - Número de telefone (veja formatos aceitos abaixo)</li>
                  <li><strong>2ª coluna: nome</strong> (opcional) - Nome do destinatário</li>
                  <li><strong>3ª coluna em diante:</strong> (opcional) - Variáveis do template (variavel_1, variavel_2, etc.)</li>
                </ul>
                <div className="mt-3 p-3 bg-info/10 border border-info/20 rounded-lg">
                  <p className="text-accent-1 text-xs font-semibold mb-2">
                    💡 Formatos de telefone aceitos:
                  </p>
                  <ul className="text-accent-1/80 text-xs space-y-1 ml-4">
                    <li>• <strong>Com DDI:</strong> +5511999999999 ou 5511999999999</li>
                    <li>• <strong>Sem DDI:</strong> 11999999999 (o código +55 será adicionado automaticamente)</li>
                    <li>• Use apenas números (sem espaços, traços ou parênteses)</li>
                  </ul>
                </div>
                <div className="mt-3 p-2 bg-info/10 border border-info/20 rounded">
                  <p className="text-accent-1/80 text-xs mb-2">
                    <strong>💡 Dica:</strong> Baixe um modelo pronto para facilitar:
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={downloadCSVTemplate}
                      className="flex items-center gap-1 px-3 py-1.5 bg-accent-1 hover:bg-accent-1/90 text-white text-xs rounded transition-colors"
                    >
                      📥 Modelo CSV
                    </button>
                    <button
                      type="button"
                      onClick={downloadXLSXTemplate}
                      className="flex items-center gap-1 px-3 py-1.5 bg-accent-1 hover:bg-accent-1/90 text-white text-xs rounded transition-colors"
                    >
                      📥 Modelo XLSX
                    </button>
                  </div>
                </div>
              </div>

              {csvPreview.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-text-2 mb-2">
                    Preview do CSV
                  </label>
                  <div className="bg-bg-2 rounded-lg p-4 overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <tbody>
                        {csvPreview.map((row, idx) => (
                          <tr key={idx} className={idx === 0 ? 'font-semibold' : ''}>
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="px-2 py-1 border border-border-1">
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Schedule */}
          {currentStep === 'schedule' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text-2 mb-4">
                  Quando deseja enviar as mensagens?
                </label>

                <div className="space-y-3">
                  {/* Immediate */}
                  <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-bg-2 transition-colors">
                    <input
                      type="radio"
                      name="scheduleType"
                      value="immediate"
                      checked={config.scheduleType === 'immediate'}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          scheduleType: e.target.value as ScheduleType,
                        }))
                      }
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-text-1">Enviar Imediatamente</p>
                      <p className="text-sm text-text-2 mt-1">
                        A campanha iniciará assim que você confirmar
                      </p>
                    </div>
                  </label>

                  {/* Scheduled - DISABLED */}
                  <label className="flex items-start p-4 border-2 rounded-lg opacity-50 cursor-not-allowed bg-bg-2">
                    <input
                      type="radio"
                      name="scheduleType"
                      value="scheduled"
                      checked={false}
                      disabled={true}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-text-2">Agendar Envio</p>
                      <p className="text-sm text-warning mt-1 mb-3 font-medium">
                        🚧 Funcionalidade em desenvolvimento
                      </p>
                      <p className="text-xs text-text-3">
                        Por enquanto, todas as campanhas são enviadas imediatamente após serem iniciadas
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <span className="text-warning">ℹ️</span>
                  <div className="text-sm text-warning">
                    <p className="font-semibold mb-1">Nota sobre agendamento:</p>
                    <p>
                      Campanhas agendadas podem ser canceladas antes do horário de início. Após o
                      início, não é possível cancelar mensagens já enviadas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Review */}
          {currentStep === 'review' && (
            <div className="space-y-6">
              <div className="bg-bg-2 rounded-lg p-6 space-y-4">
                <h3 className="font-semibold text-text-1 text-lg mb-4">Revisão Final</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-text-2 mb-1">Template</p>
                    <p className="font-medium text-text-1">{template.name}</p>
                  </div>

                  <div>
                    <p className="text-xs text-text-2 mb-1">Nome da Execução</p>
                    <p className="font-medium text-text-1">{config.executionName}</p>
                  </div>

                  <div>
                    <p className="text-xs text-text-2 mb-1">Destinatários Válidos</p>
                    <p className="font-medium text-success text-2xl">{validRecipients}</p>
                  </div>

                  <div>
                    <p className="text-xs text-text-2 mb-1">Tipo de Envio</p>
                    <p className="font-medium text-text-1">
                      {config.scheduleType === 'immediate' ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-success/10 text-success">
                          ⚡ Imediato
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-info/10 text-accent-1">
                          🕐 Agendado
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {config.scheduleType === 'scheduled' && (
                  <div className="pt-4 border-t border-border-1">
                    <p className="text-xs text-text-2 mb-1">Data/Hora Agendada</p>
                    <p className="font-medium text-text-1">
                      {new Date(config.scheduledAt).toLocaleString('pt-BR', {
                        dateStyle: 'long',
                        timeStyle: 'short',
                      })}
                    </p>
                  </div>
                )}

                {Object.keys(getEditableGlobalParams()).length > 0 && (
                  <div className="pt-4 border-t border-border-1">
                    <p className="text-xs text-text-2 mb-2">Parâmetros Globais (Header/Footer)</p>
                    <div className="bg-bg-1 rounded p-3 space-y-1">
                      {Object.entries(getEditableGlobalParams()).map(([key, value]) => (
                        <div key={key} className="flex items-center text-sm">
                          <span className="font-mono text-text-2">{key}:</span>
                          <span className="ml-2 text-text-1">{value}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-text-3 mt-2">
                      * Parâmetros do body serão preenchidos pelo CSV
                    </p>
                  </div>
                )}
              </div>

              {/* Dispatch Limit Indicator */}
              <DispatchLimitIndicator
                wabaAccountId={template.wabaAccountId}
                recipientCount={validRecipients}
                isLoading={isLoadingCapacity}
                capacityInfo={capacityInfo}
              />

              <div className="bg-info/10 border border-info/20 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <span className="text-accent-1">ℹ️</span>
                  <div className="text-sm text-accent-1">
                    <p>
                      Ao confirmar, a campanha será{' '}
                      {config.scheduleType === 'immediate' ? 'iniciada imediatamente' : 'agendada'}{' '}
                      e você poderá acompanhar o progresso em tempo real na página de campanhas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-bg-2 border-t border-border-1 px-6 py-4 flex items-center justify-between">
          <div>
            {currentStep !== 'info' && (
              <button
                onClick={prevStep}
                disabled={isSubmitting}
                className="px-4 py-2 text-text-2 hover:text-text-1 font-medium disabled:opacity-50"
              >
                ← Voltar
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-text-2 hover:text-text-1 font-medium disabled:opacity-50"
            >
              Cancelar
            </button>

            {currentStep !== 'review' ? (
              <button
                onClick={nextStep}
                disabled={isSubmitting}
                className="px-6 py-2 bg-accent-1 text-white rounded-lg hover:bg-accent-1/90 font-medium disabled:opacity-50"
              >
                Próximo →
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  !config.file ||
                  isLoadingCapacity ||
                  (capacityInfo && !capacityInfo.canDispatch) ||
                  isDispatchBlocked
                }
                className="px-6 py-2 bg-success text-white rounded-lg hover:bg-success/90 font-medium disabled:opacity-50 flex items-center gap-2"
                title={
                  isDispatchBlocked
                    ? 'Conta com qualidade baixa - execução bloqueada'
                    : capacityInfo && !capacityInfo.canDispatch
                    ? 'Você não tem capacidade suficiente para este disparo'
                    : undefined
                }
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Executando...
                  </>
                ) : isLoadingCapacity ? (
                  <>
                    <span className="animate-spin">⏳</span>
                    Verificando limite...
                  </>
                ) : isQualityBlocked ? (
                  <>
                    <span>🚫</span>
                    Qualidade Baixa
                  </>
                ) : capacityInfo && !capacityInfo.canDispatch ? (
                  <>
                    <span>🚫</span>
                    Limite Excedido
                  </>
                ) : (
                  <>
                    <span>✓</span>
                    Confirmar e Executar
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Duplicate Warning Modal */}
      {showDuplicateWarning && duplicateData && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-bg-1 rounded-lg shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-bold text-warning mb-2">
                Atenção: Alta Duplicação Detectada
              </h3>
              <p className="text-sm text-text-2">
                Você está prestes a enviar mensagens para destinatários que receberam sua última campanha recentemente.
              </p>
            </div>

            <div className="bg-warning/10 border-2 border-warning/30 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <p className="text-xs text-text-2 mb-1">Destinatários Duplicados</p>
                  <p className="text-3xl font-bold text-warning">
                    {duplicateData.duplicatePercentage.toFixed(1)}%
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-text-2 mb-1">Números Duplicados</p>
                  <p className="text-2xl font-bold text-text-1">
                    {duplicateData.duplicateCount} / {duplicateData.totalNewRecipients}
                  </p>
                </div>
              </div>

              <div className="border-t border-warning/30 pt-3 text-xs text-text-2">
                <p><strong>Última campanha:</strong> {duplicateData.lastCampaignName}</p>
                <p className="mt-1">
                  <strong>Enviada em:</strong>{' '}
                  {duplicateData.lastCampaignDate
                    ? new Date(duplicateData.lastCampaignDate).toLocaleDateString('pt-BR')
                    : 'N/A'}
                </p>
              </div>
            </div>

            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-6">
              <p className="text-xs text-warning">
                <strong>💡 Importante:</strong> Enviar mensagens duplicadas pode irritar seus destinatários e prejudicar sua taxa de engajamento.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDuplicateWarning(false);
                  setDuplicateData(null);
                  setUserConfirmed(false);
                }}
                className="flex-1 px-4 py-3 bg-bg-3 text-text-1 rounded-lg hover:bg-bg-3/80 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  setShowDuplicateWarning(false);
                  setUserConfirmed(true);
                  // Re-trigger submit with confirmation
                  setTimeout(() => handleSubmit(), 100);
                }}
                className="flex-1 px-4 py-3 bg-warning text-white rounded-lg hover:bg-warning/90 font-medium transition-colors"
              >
                Continuar Mesmo Assim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
